import * as React from 'react';
import { SubmissionDataTabsDefinition, SubmissionTabTypes } from 'types/types.type';
import {
    STATUS_TYPES,
    SUBMISSION_STATUS_NO_SUBMISSIONS,
    SUBMISSION_UNREAD_MESSAGES_NO_SUBMISSIONS,
    AUTH_GROUPS,
} from 'constants/index';
import { dateSort } from '../../../components/LrGrid';
import moment from 'moment';
import { formatDollar } from '../../../helpers/formatters';
import { ValueGetterParams } from 'ag-grid-enterprise';
import { PrioritySubmissionsView } from './CustomTabViews';

const PrioritySubmissionStatuses = [
    STATUS_TYPES.INCOMPLETE_SUBMISSION,
    STATUS_TYPES.READY_FOR_CLEARING,
    STATUS_TYPES.READY_FOR_UNDERWRITING,
    STATUS_TYPES.READY_TO_QUOTE,
    STATUS_TYPES.QUOTED,
    STATUS_TYPES.REQUOTE_REQUESTED,
    STATUS_TYPES.BIND_REQUEST_REVIEW,
    STATUS_TYPES.BOUND,
    STATUS_TYPES.READY_TO_ISSUE,
    STATUS_TYPES.ISSUED,
];

export const PPAmTabDataDefinition: SubmissionDataTabsDefinition = {
    tabs: [
        {
            tabName: 'Priority',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: PrioritySubmissionStatuses,
                },
            ],
            customRenderer: (props) => React.createElement(PrioritySubmissionsView, props),
        },
        {
            tabName: 'Clearance',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.READY_FOR_CLEARING],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Clearance'),
        },
        {
            tabName: 'To Quote',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.READY_TO_QUOTE],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('To Quote'),
        },
        {
            tabName: 'Bind Request',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.BIND_REQUEST_REVIEW],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Bind Request'),
        },
        {
            tabName: 'Requote',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.REQUOTE_REQUESTED],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Requote'),
        },
        {
            tabName: 'To Issue',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.BOUND, STATUS_TYPES.READY_TO_ISSUE],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Ready To Issue'),
        },
        {
            tabName: 'Issued',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ISSUED],
                },
            ],
            isInForce: true,
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Issued'),
        },
        {
            tabName: 'Endorse Review',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ENDORSEMENT_REQUEST_REVIEW],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Endorsement Request Review'),
        },
        {
            tabName: 'Expiring',
            filters: [
                {
                    type: SubmissionTabTypes.EXPIRING,
                    numDays: 90,
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Expiring'),
        },
        { tabName: 'All Submitted', filters: [{ type: SubmissionTabTypes.STATUS, statusList: [STATUS_TYPES.ALL] }] },
        {
            tabName: 'Unread Messages',
            filters: [
                {
                    type: SubmissionTabTypes.PROPERTY,
                    propertyFilter: 'hasAgentMessages',
                    propertyValue: true,
                },
            ],
            emptyText: SUBMISSION_UNREAD_MESSAGES_NO_SUBMISSIONS,
        },
    ],
};

export const PPUwTabDataDefinition: SubmissionDataTabsDefinition = {
    tabs: [
        {
            tabName: 'Priority',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: PrioritySubmissionStatuses,
                },
            ],
            customRenderer: (props) => React.createElement(PrioritySubmissionsView, props),
        },
        {
            tabName: 'New',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.READY_FOR_UNDERWRITING],
                },
                {
                    type: SubmissionTabTypes.PROPERTY,
                    propertyFilter: 'isRenewal',
                    propertyValue: false,
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Ready For Underwriting'),
            additionalColumns: [
                {
                    columnOrder: 8,
                    headerName: 'Base Premium',
                    field: 'basePremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '0';
                    },
                },
            ],
        },
        {
            tabName: 'Renewing',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.READY_FOR_UNDERWRITING],
                },
                {
                    type: SubmissionTabTypes.PROPERTY,
                    propertyFilter: 'isRenewal',
                    propertyValue: true,
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Renewing'),
        },
        {
            tabName: 'Requote',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.REQUOTE_REQUESTED],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Requote Requested'),
        },
        {
            tabName: 'Endt Review',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ENDORSEMENT_REQUEST_REVIEW],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Endorsement Review'),
        },
        {
            tabName: 'Referred',
            filters: [
                {
                    type: SubmissionTabTypes.PROPERTY,
                    propertyFilter: 'hasOpenReferredLocations',
                    propertyValue: true,
                },
            ],
        },
        {
            tabName: 'To Quote',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.READY_TO_QUOTE],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('To Quote'),
        },
        {
            tabName: 'Quoted',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.QUOTED],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Quoted'),
            additionalColumns: [
                {
                    colId: 'Quoted Date',
                    headerName: 'Quoted Date',
                    valueGetter: (params: ValueGetterParams) => {
                        let datStr = params.data.firstQuoteDate
                            ? moment(params.data.firstQuoteDate).format('MM/DD/YYYY')
                            : '';
                        datStr += params.data.hasMultipleQuotes ? '*' : '';
                        return datStr;
                    },
                    comparator: dateSort,
                    columnOrder: 3,
                },
                {
                    columnOrder: 9,
                    headerName: 'Prop Quoted Premium',
                    field: 'quotedPremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '';
                    },
                },
                {
                    columnOrder: 10,
                    headerName: 'GL Quoted Premium',
                    field: 'glQuotedPremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '';
                    },
                },
            ],
            childTabs: [
                {
                    tabName: 'New',
                    filters: [
                        {
                            type: SubmissionTabTypes.PROPERTY,
                            propertyFilter: 'isRenewal',
                            propertyValue: false,
                        },
                    ],
                },
                {
                    tabName: 'Renewing',
                    filters: [
                        {
                            type: SubmissionTabTypes.PROPERTY,
                            propertyFilter: 'isRenewal',
                            propertyValue: true,
                        },
                    ],
                },
                {
                    tabName: 'All',
                    filters: [
                        {
                            type: SubmissionTabTypes.STATUS,
                            statusList: [STATUS_TYPES.ALL],
                        },
                    ],
                },
            ],
        },
        {
            tabName: 'Bind Request',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.BIND_REQUEST_REVIEW],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Bind Request'),
            additionalColumns: [
                {
                    columnOrder: 8,
                    headerName: 'Prop Quoted Premium',
                    field: 'quotedPremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '';
                    },
                },
                {
                    columnOrder: 9,
                    headerName: 'GL Quoted Premium',
                    field: 'glQuotedPremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '';
                    },
                },
            ],
        },
        {
            tabName: 'To Issue',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.BOUND, STATUS_TYPES.READY_TO_ISSUE],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Ready To Issue'),
        },
        {
            tabName: 'Issued',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ISSUED],
                },
            ],
            isInForce: true,
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Issued'),
            additionalColumns: [
                {
                    columnOrder: 8,
                    headerName: 'Bound Premium',
                    field: 'grossPremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '';
                    },
                },
                {
                    columnOrder: 9,
                    headerName: 'Policy Number',
                    field: 'policyNumber',
                },
            ],
            childTabs: [
                {
                    tabName: 'LUM4',
                    filters: [
                        {
                            type: SubmissionTabTypes.PROPERTY,
                            propertyFilter: 'treatyOrder',
                            propertyValue: '1',
                        },
                    ],
                    isInForce: true,
                },
                {
                    tabName: 'LUM3',
                    filters: [
                        {
                            type: SubmissionTabTypes.PROPERTY,
                            propertyFilter: 'treatyOrder',
                            propertyValue: '2',
                        },
                    ],
                    isInForce: true,
                },
                {
                    tabName: 'All',
                    filters: [
                        {
                            type: SubmissionTabTypes.STATUS,
                            statusList: [STATUS_TYPES.ALL],
                        },
                    ],
                },
            ],
        },
        {
            tabName: 'Clearance',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.READY_FOR_CLEARING],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Clearance'),
        },
        {
            tabName: 'Expiring',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ISSUED],
                },
                {
                    type: SubmissionTabTypes.PROPERTY,
                    propertyFilter: 'isExpiring',
                    propertyValue: true,
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Expiring'),
        },
        {
            tabName: 'All',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ALL],
                },
            ],
            additionalColumns: [
                {
                    columnOrder: 8,
                    headerName: 'Base Premium',
                    field: 'basePremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '$0';
                    },
                },
                {
                    columnOrder: 9,
                    headerName: 'Quoted Premium',
                    field: 'quotedPremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '$0';
                    },
                },
                {
                    columnOrder: 10,
                    headerName: 'Bound Premium',
                    field: 'grossPremium',
                    valueFormatter: ({ value }: any) => {
                        return value ? formatDollar(value, 0) : '$0';
                    },
                },
            ],
        },
        {
            tabName: 'Unread',
            filters: [
                {
                    type: SubmissionTabTypes.PROPERTY,
                    propertyFilter: 'hasAgentMessages',
                    propertyValue: true,
                },
            ],
            emptyText: SUBMISSION_UNREAD_MESSAGES_NO_SUBMISSIONS,
        },
    ],
};

export const PPAdminTabDataDefinition: SubmissionDataTabsDefinition = {
    tabs: [
        ...PPUwTabDataDefinition.tabs,
        {
            tabName: 'Incomplete',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.INCOMPLETE_SUBMISSION],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Incomplete'),
        },
    ],
};

export const PPCarrierTabDataDefinition: SubmissionDataTabsDefinition = {
    defaultTab: 'Referred',
    tabs: [
        {
            tabName: 'Referred',
            filters: [
                {
                    type: SubmissionTabTypes.PROPERTY,
                    propertyFilter: 'hasOpenReferredLocations',
                    propertyValue: true,
                },
            ],
        },
        {
            tabName: 'All',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ALL],
                },
            ],
        },
    ],
};

export const PPAuditTabDataDefinition: SubmissionDataTabsDefinition = {
    defaultTab: 'Referred',
    tabs: [
        {
            tabName: 'All',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ALL],
                },
            ],
        },
    ],
};

export const PPAgentTabDataDefinition: SubmissionDataTabsDefinition = {
    defaultTab: 'Quoted',
    tabs: [
        {
            tabName: 'Submitted',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.ALL],
                },
            ],
            disableCount: true,
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Submitted'),
        },
        {
            tabName: 'Quoted',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.QUOTED],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Quoted'),
        },
        {
            tabName: 'Requote',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.REQUOTE_REQUESTED],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Requote Requested'),
        },
        {
            tabName: 'In Force',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.BOUND, STATUS_TYPES.ISSUED],
                },
            ],
            isInForce: true,
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('In Force'),
        },
        {
            tabName: 'Expiring',
            filters: [
                {
                    type: SubmissionTabTypes.EXPIRING,
                    numDays: 90,
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Expiring'),
        },
        {
            tabName: 'Incomplete',
            filters: [
                {
                    type: SubmissionTabTypes.STATUS,
                    statusList: [STATUS_TYPES.INCOMPLETE_SUBMISSION],
                },
            ],
            emptyText: SUBMISSION_STATUS_NO_SUBMISSIONS('Incomplete'),
        },
        {
            tabName: 'Unread Messages',
            filters: [
                {
                    type: SubmissionTabTypes.PROPERTY,
                    propertyFilter: 'hasLumenMessages',
                    propertyValue: true,
                },
            ],
            emptyText: SUBMISSION_UNREAD_MESSAGES_NO_SUBMISSIONS,
        },
    ],
};

// Function to get the appropriate tab definition based on user group
export const getPPSubmissionTabDefinition = (userGroup: string): SubmissionDataTabsDefinition => {
    switch (userGroup) {
        case AUTH_GROUPS.ADMIN:
            return PPAdminTabDataDefinition;
        case AUTH_GROUPS.UNDERWRITER:
            return PPUwTabDataDefinition;
        case AUTH_GROUPS.ACCOUNT_MANAGER:
            return PPAmTabDataDefinition;
        case AUTH_GROUPS.CARRIER_REP:
            return PPCarrierTabDataDefinition;
        case AUTH_GROUPS.AUDIT_REP:
            return PPAuditTabDataDefinition;
        case AUTH_GROUPS.AGENT:
        default:
            return PPAgentTabDataDefinition;
    }
};
